import { _, uuid, defineAppConfig } from '@zto/zpage'

import { HTTP_STATUS_CODES, REQUEST_CODES } from '../../consts'

import { ffbs } from './ffbs'

import type { App, AppAppApiConfig, ApiRequestConfig } from '@zto/zpage'
import { getAppAuthData } from '../../utils'

export default defineAppConfig<AppAppApiConfig>((app: App) => {
  return {
    baseUrl: app.env.apiUrl,

    inner: { timeout: 50000 },

    ffbs,

    request: async (config: ApiRequestConfig) => {
      if (!config) return

      const env = app.env

      let { url, api, action, params, data } = config
      action = action || api || url

      if (!action) return

      if (typeof action === 'string') action = { url: action }
      if (typeof action.api === 'string') action.url = action.url || action.api
      else if (action.api) action = { ...action.api, ...action }

      // api汇总
      let summaryAction: any = action.summaryApi
      if (typeof summaryAction === 'string') summaryAction = { url: summaryAction }

      // 如果设置了mockData则返回mock数据
      if (env.name === 'dev' && action.mockData) {
        let mockData = _.deepClone(action.mockData)
        if (_.isFunction(action.mockData)) mockData = action.mockData(app.useContext({ config }))
        if (action.responseTransform) mockData = action.responseTransform(mockData, action)
        return mockData
      }

      let postData = params || data || {}

      if (action.type === 'query' || action.type === 'fuzzy-select' || action.type === 'export') {
        const { pageIndex, pageSize, pageSort, noPager } = config

        // 如果设置了uniqueApiParamProp且uniqueApiParamProp存在值，则只传输uniqueApiParamProp
        if (action.uniqueApiParamProp) {
          const uniquePropData = postData[action.uniqueApiParamProp] || postData?.parameter?.[action.uniqueApiParamProp]
          if (!_.isEmpty(uniquePropData, { emptyArray: true })) {
            // 如果是导出类型，参数在parameter中
            if (action.type === 'export') {
              postData = { ...postData, parameter: { [action.uniqueApiParamProp]: uniquePropData } }
            } else {
              postData = { [action.uniqueApiParamProp]: uniquePropData }
            }
          }
        }

        if (noPager) {
          postData = { ...(postData || {}) }
        } else {
          postData = { pageIndex, pageSize, pageSort, ...postData }
        }
      }

      if (action.transform?.request) postData = action.transform?.request(postData, action)
      if (action.requestTransform) postData = action.requestTransform(postData, action)

      // 根据url获取命名空间
      if (_.isString(action.url)) {
        const { ns, url } = app.parseApiUrl(action.url)

        if (ns) {
          action.ns = ns
          action.url = url
        }
      }

      const fetchConfig: any = {
        url: action.url,
        method: action.method || 'POST',
        data: postData,
        rawConfig: config
      }

      // 自定义超时
      if (action.timeout) {
        fetchConfig.timeout = action.timeout
      }

      if (['GET', 'get'].includes(fetchConfig.method)) {
        fetchConfig.params = postData
      }

      let reqApi = app.api
      if (action.ns) {
        reqApi = app.apis[action.ns] || app.apis[`${action.ns}Api`]

        if (!reqApi) throw new Error(`未找到Api命名空间${action.ns}。`)
      }

      let res = await reqApi.fetch(fetchConfig)

      let summaryRes: any
      if (summaryAction) {
        const summaryFetcConfig: any = {
          url: summaryAction.url,
          method: summaryAction.method || 'POST',
          data: postData,
          rawConfig: config
        }

        if (['GET', 'get'].includes(summaryFetcConfig.method)) {
          summaryFetcConfig.params = postData
        }

        summaryRes = await reqApi.fetch(summaryFetcConfig)

        if (summaryAction.responseTransform) summaryRes = summaryAction.responseTransform(summaryRes, summaryAction)

        // 查询
        res = {
          ...res,
          __summary: summaryRes // 注意：__summary预定字段名称（不能修改）
        }
      }

      if (action.transform?.response) res = action.transform?.response(res, action)
      if (action.responseTransform) res = action.responseTransform(res, action)

      if (action.type === 'query') {
        app.emits('trace', {
          event_code: 'query',
          event_data: { action: config.action, postData }
        })
      }
      if (action.type === 'export') {
        app.emits('trace', {
          event_code: 'export',
          event_data: { action: config.action, params: config.params, postData }
        })
      }

      return res
    },

    interceptors: {
      beforeRequest(config: any) {
        const Progress = app.useProgress()
        const env = app.env

        Progress.start()

        const authData = getAppAuthData()

        if (env.extraHeaders) config.headers = { ...env.extraHeaders, ...config.headers }

        config.headers['x-zop-ns'] = env.appNs
        config.headers['x-request-id'] = uuid()
        config.headers['x-request-source'] = env.requestSource || ''

        config.headers['x-portal-role-code'] = authData.portalRoleCode || ''
        config.headers['x-user-dept-position'] = authData.positionCode || ''
        config.headers['x-user-dept'] = authData.deptCode || ''

        config.headers['x-extend'] = 1 // 门户特需字段

        const pageName = app.currentRoute.value?.meta?.name
        if (pageName) config.headers['x-page-name'] = pageName

        // 测试时可用其他token替换存储token
        if (!config.headers['x-iam-token']) {
          const token = app.token.getAccessToken()
          if (token) config.headers['x-iam-token'] = token
        }

        return config
      },

      requestError(error: any) {
        const Progress = app.useProgress()
        Progress.done()

        return Promise.reject(error)
      },

      afterResponse: async (response: any) => {
        // app.emits('trace', {
        //   event_code: 'response',
        //   event_data: { response }
        // })
        const Progress = app.useProgress()
        const { Message } = app.useMessage()

        const env = app.env

        Progress.done()

        const cfg = response.config
        const res = response.data

        // 如果是mock环境，则直接设置status为true
        if (env.name === 'mock') res.status = true

        // 如果设置了mockData则返回mock数据
        if (env.name === 'dev' && cfg.mockData) {
          res.status = true
          res.result = cfg.mockData
        }

        if (cfg.responseType === 'blob') return response
        if (res.status) return response
        const flag = await _dealWithFalseResponse(response, app)

        // 为true继续，false则直接返回
        if (!flag) return Promise.reject(res)

        // 设置isSlient标志不提示错误
        if (!cfg.isSilent && !cfg.rawConfig?.isSilent) {
          Message.error({ type: 'error', message: res.message, showClose: true })
        }

        return Promise.reject(res)
      },

      afterFetchResponse: (response: any) => {
        return response.data?.result
      },

      responseError: (error: any) => {
        // app.emits('trace', {
        //   event_code: 'responseError',
        //   event_data: { error }
        // })
        const Progress = app.useProgress()
        const { MessageBox } = app.useMessage()

        Progress.done()
        MessageBox.close()

        MessageBox.confirm('网络开小差了，请稍后重试～', '提示', {
          type: 'error',
          showCancelButton: false
        })

        return Promise.reject(error)
      }
    }
  }
})

/** 处理token问题 */
async function _dealWithFalseResponse(response: any, app: App) {
  const cfg = response.config || {}
  const res = response.data || {}

  const statusCode = res?.statusCode

  // 埋点接口不作处理
  if (cfg.reqCode === REQUEST_CODES.TRACE) return true

  if (!Object.values(HTTP_STATUS_CODES).includes(statusCode)) return true

  const { MessageBox } = app.useMessage()

  let logoutMessage = res.message || '登录已失效，请重新登录'

  // 是否执行登出操作，默认执行 （0: 不登出，1: 登出）
  let logoutFlag = 1

  switch (statusCode) {
    case HTTP_STATUS_CODES.EMPTY_PARAM:
      // Token为空直接登出
      if (!app.token.getAccessToken()) return app.logout()
      return true
    case HTTP_STATUS_CODES.IAM_TOKEN_EXPIRED:
    case HTTP_STATUS_CODES.SSO_TOKEN_EXPIRED:
      await app.token
        .refresh()
        .then((res: any) => {
          /** 刷新token成功，不再登出 */
          logoutFlag = 0

          // 如若获取用户信息接口失败，则直接刷新
          if (cfg.url === 'base.getCurrentUserInfo' || cfg.reqCode === REQUEST_CODES.AUTH_GET_USER_INFO) {
            location.reload()
          }
        })
        .catch((err: any) => {
          logoutMessage = '登录过期'
        })
      break
    case HTTP_STATUS_CODES.JWT_TOKEN_INVALID:
      logoutMessage = '登录无效'
      break
  }

  if (
    [HTTP_STATUS_CODES.INVALID_CODE, HTTP_STATUS_CODES.USER_DISABLED, HTTP_STATUS_CODES.SSO_ERROR].includes(statusCode)
  ) {
    logoutFlag = 1
    logoutMessage = `${res.message || '账号验证错误'}`
  }

  if (!logoutFlag) return false

  await MessageBox.confirm(`${logoutMessage}，请重新登陆`, '提示', {
    type: 'error',
    showCancelButton: false,
    closeOnClickModal: false
  }).then(() => {
    // 跳转登录页
    return app.logout()
  })

  return true
}
